import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import { ITableFilter } from '@/common/components/controls/table/TableFilter';
import { Filter } from '@/common/models';
import Exception from '@/common/models/Exception';
import BooleanFilter from '@/common/models/query-params/BooleanFilter';
import DateFilter from '@/common/models/query-params/DateFilter';
import DateFilterComparisonOptions from '@/common/models/query-params/DateFilterComparisonOptions';
import ListFilter from '@/common/models/query-params/ListFilter';
import NumericFilter from '@/common/models/query-params/NumericFilter';
import UUIDFilter from '@/common/models/query-params/UUIDFilter';
import { capitalizeTranslation, DASH, i } from '@/globals';
import Validatable from '@/validator/Validatable';

export interface IUserView {
  name: string;
  value: string;
  isDefault: boolean;
}

export default class UserView extends Validatable {
  name: string;
  value: string;
  isDefault: boolean;
  isApplied: boolean;

  constructor(obj: IUserView) {
    super();

    this.name = obj?.name ?? capitalizeTranslation('names.default');
    this.value = obj?.value;
    this.isDefault = obj?.isDefault ?? true;
    this.isApplied = false;
  }

  async getSummary(availableFilters: ITableFilter[]): Promise<string[]> {
    if (!availableFilters?.length) return [];

    // we unfortunately allowed to save empty strings as a filter in the DB. Handle this case.
    if (!this.value) return [];

    const serializedFilters = this.value?.split('|') ?? [];
    const sanitizedSerializedFilters = serializedFilters.filter((f) => f); // remove empty strings. there is no summary to get from them
    const summary = [];

    for await (const filter of sanitizedSerializedFilters) {
      const [key] = filter.split('~');
      const activeFilter = availableFilters.find((f) => f.property === key);
      const filterInstanceType = this.getFilterInstanceType(activeFilter);
      activeFilter.instance = filterInstanceType.deserializeFromQueryString(activeFilter, filter);

      const formattedValues = (await this.getFormattedValues(activeFilter))?.filter((f) => !!f);
      let compactFormattedValues = '';

      if (formattedValues.length === 0) {
        // if the formatted values are empty, it means the user wants to save a filter without a set value, show a dash for the summary
        compactFormattedValues = DASH;
      } else if (formattedValues.length > 3) {
        // ellipse the values if there are too many
        compactFormattedValues = `${formattedValues.join(', ').slice(0, 10)}... +${formattedValues.length - 1}`;
      } else {
        compactFormattedValues = formattedValues.join(', ');
      }

      summary.push(`${activeFilter.label}: ${compactFormattedValues}`);
    }

    return summary;
  }

  private async getFormattedValues(activeFilter): Promise<string[]> {
    const formattedValues = [];

    if (activeFilter.instance?.values) {
      for await (const value of activeFilter.instance.values) {
        let source = activeFilter?.source;
        const { sourceID, sourceLabel } = activeFilter;

        if (typeof activeFilter?.source === 'function') {
          source = await activeFilter.source();
        }

        const selectedSource = source?.find((s) => s[sourceID] == value); // Don't use ===, we have ids as strings, some as integers
        formattedValues.push(selectedSource?.[sourceLabel] ?? value);
      }
    }

    if (activeFilter.instance instanceof NumericFilter) {
      const { firstValue, secondValue, comparisonOption } = activeFilter.instance;

      let str = '';
      if (comparisonOption && firstValue) {
        str += `${comparisonOption.label} ${firstValue}`;

        if (secondValue) {
          str += ` - ${secondValue}`;
        }

        formattedValues.push(str);
      }
    }

    if (activeFilter.instance instanceof BooleanFilter) {
      const { value } = activeFilter.instance;
      const boolValue = value ? 'yes' : 'no';
      formattedValues.push(capitalizeTranslation(`actions.${boolValue}`));
    }

    if (activeFilter.instance instanceof DateFilter) {
      const {
        comparisonOption,
        durationValue,
        durationUnit,
        firstDate,
        secondDate,
        before,
        after,
        rangeStart,
        rangeEnd,
      } = activeFilter.instance;

      let str = '';

      switch (comparisonOption) {
        case DateFilterComparisonOptions.WithinTheLast.id:
          str += `${i('messages.withinTheLast')} ${durationValue} ${i(`names.${durationUnit}`, Number(durationValue))}`;
          break;
        case DateFilterComparisonOptions.MoreThan.id:
          str += `${i('messages.moreThan')} ${durationValue} ${i(`names.${durationUnit}`, Number(durationValue))}`;
          break;
        case DateFilterComparisonOptions.Between.id:
          str += `${i('messages.between')} ${firstDate.toLocaleString()} ${i('conjuctions.and')} ${secondDate.toLocaleString()}`;
          break;
        case DateFilterComparisonOptions.Before.id:
          str += `${i('states.before')} ${before.toLocaleString()}`;
          break;
        case DateFilterComparisonOptions.After.id:
          str += `${i('states.after')} ${after.toLocaleString()}`;
          break;
        case DateFilterComparisonOptions.InRange.id:
          str += `${i('messages.inRange')} ${rangeStart} ${i('adverbs.to')} ${rangeEnd}`;
          break;
        default:
          break;
      }

      formattedValues.push(str);
    }

    return formattedValues;
  }

  private getFilterInstanceType(f) {
    if (f == null) throw new Exception('Invalid view');

    if (f.type === TableColumnTypes.list || f.source != null) {
      return ListFilter;
    }

    switch (f.type) {
      case TableColumnTypes.boolean:
        return BooleanFilter;
      case TableColumnTypes.date:
      case TableColumnTypes.datetime:
        return DateFilter;
      case TableColumnTypes.string:
        return Filter;
      case TableColumnTypes.float:
      case TableColumnTypes.integer:
      case TableColumnTypes.currency:
      case TableColumnTypes.percentage:
        return NumericFilter;
      case TableColumnTypes.uuid:
        return UUIDFilter;
      default:
        return Filter;
    }
  }
}
